import React, { useCallback, useEffect, useState } from 'react';
import { LLP_PROFILE } from '../../../constants/mcaEntity';
import { ChargesRegister } from '../Registers';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const token = () => localStorage.getItem('token');
const authHeaders = () => ({ Authorization: `Bearer ${token()}` });
const jsonHeaders = () => ({ ...authHeaders(), 'Content-Type': 'application/json' });

const PARTNER_MEETING_TYPES = ['PARTNERS', 'PARTNERS_CIRCULATION'];
const SUB_TYPES = LLP_PROFILE.meetings.types; // ["Partners' Meeting", 'Meeting by Circulation', 'Adjourned Meeting']
const DECISION_MODES = LLP_PROFILE.meetings.decisionModes;
const STATUSES = ['Draft', 'Notice Issued', 'Held', 'Minuted', 'Cancelled'];

function fmtDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(String(iso))) return '—';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
}
const isCirculation = (sub) => /circulation/i.test(String(sub || ''));
const storeType = (sub) => (isCirculation(sub) ? 'PARTNERS_CIRCULATION' : 'PARTNERS');

// ═══════════════════════════════════════════════════════════════════════
// LLP REGISTER  — Partners' Meetings + LLP-applicable statutory registers
// ═══════════════════════════════════════════════════════════════════════
export default function LlpRegister() {
  const [llps, setLlps] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [tab, setTab] = useState('meetings');

  useEffect(() => {
    fetch(`${BASE}/api/mca/companies`, { headers: authHeaders() })
      .then(r => r.json())
      .then(list => {
        const only = (Array.isArray(list) ? list : []).filter(c => /\b(llp|limited liability partnership)\b/i.test(c.company_type || ''));
        setLlps(only);
        if (only[0]) setCompanyId(String(only[0].id));
      })
      .catch(() => setLlps([]));
  }, []);

  const company = llps.find(c => String(c.id) === String(companyId));

  return (
    <div>
      <div className="mb-4">
        <p className="font-bold text-lg text-[#0f1f45]">LLP Register</p>
        <p className="text-xs text-gray-400">Partners' meetings & decisions (governed by the LLP Agreement — non-statutory) and the LLP-applicable statutory registers.</p>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <select value={companyId} onChange={e => setCompanyId(e.target.value)}
          className="px-3 py-2 rounded-lg border border-gray-200 text-sm min-w-[260px]">
          <option value="">Select an LLP…</option>
          {llps.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
        </select>
        {llps.length === 0 && <span className="text-xs text-amber-600">No LLPs found — add one in Company Master (type "LLP").</span>}
      </div>

      {company && (
        <>
          <div className="flex items-center gap-1 mb-5 border-b border-gray-100">
            {[['meetings', "Partners' Meetings"], ['charges', 'Charges (CHG-1 / CHG-4)']].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)}
                className={`relative px-4 py-2.5 text-xs font-semibold ${tab === k ? 'text-[#0f1f45]' : 'text-gray-400 hover:text-gray-600'}`}>
                {label}
                {tab === k && <span className="absolute left-3 right-3 -bottom-px h-[3px] rounded" style={{ background: '#FF8400' }} />}
              </button>
            ))}
          </div>

          {tab === 'meetings' && <PartnersMeetings company={company} />}
          {tab === 'charges' && (
            <div className="bg-white rounded-2xl border border-gray-100 p-2">
              <ChargesRegister company={company} entity={LLP_PROFILE} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PARTNERS' MEETINGS  — reused embedded inside Company Master's LLP tab
// ═══════════════════════════════════════════════════════════════════════
export function PartnersMeetings({ company, embedded = false }) {
  const [meetings, setMeetings] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // meeting object | 'new' | null
  const [detail, setDetail] = useState(null);

  const fetchMeetings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/api/mca/meetings?company_id=${company.id}`, { headers: authHeaders() });
      const data = await res.json();
      setMeetings((Array.isArray(data) ? data : []).filter(m => PARTNER_MEETING_TYPES.includes(m.meeting_type)));
    } catch (e) { setMeetings([]); }
    finally { setLoading(false); }
  }, [company.id]);

  useEffect(() => {
    fetchMeetings();
    fetch(`${BASE}/api/mca/companies/${company.id}/directors`, { headers: authHeaders() })
      .then(r => r.json())
      .then(d => setPartners((Array.isArray(d) ? d : []).filter(p => p.is_active !== false)))
      .catch(() => setPartners([]));
  }, [fetchMeetings, company.id]);

  const remove = async (m) => {
    if (!window.confirm('Delete this partners’ meeting record?')) return;
    await fetch(`${BASE}/api/mca/meetings/${m.id}`, { method: 'DELETE', headers: authHeaders() });
    fetchMeetings();
  };

  if (detail) {
    return <MeetingDetail meeting={detail} company={company} partners={partners}
      onBack={() => { setDetail(null); fetchMeetings(); }}
      onEdit={() => { setEditing(detail); setDetail(null); }} />;
  }

  if (editing) {
    return <MeetingForm company={company} partners={partners}
      meeting={editing === 'new' ? null : editing}
      onCancel={() => setEditing(null)}
      onSaved={(saved) => { setEditing(null); fetchMeetings(); if (saved) setDetail(saved); }} />;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-bold text-[#0f1f45]">Partners' Meetings & Decisions</p>
          <p className="text-[11px] text-gray-400">{LLP_PROFILE.meetings.note}</p>
        </div>
        <button onClick={() => setEditing('new')} className="px-3.5 py-2 rounded-lg text-xs font-semibold text-white bg-[#0f1f45]">+ Record Meeting</button>
      </div>

      <div className={`bg-white rounded-2xl border border-gray-100 overflow-hidden ${embedded ? '' : ''}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-[11px] font-semibold text-gray-500 uppercase">
              <th className="px-4 py-2.5">Date</th>
              <th className="px-4 py-2.5">Type</th>
              <th className="px-4 py-2.5">Decision mode</th>
              <th className="px-4 py-2.5">Agenda / decisions</th>
              <th className="px-4 py-2.5">Status</th>
              <th className="px-4 py-2.5"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {loading && <tr><td colSpan={6} className="px-4 py-8 text-center text-xs text-gray-400">Loading…</td></tr>}
            {!loading && meetings.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-xs text-gray-400">No partners' meetings recorded yet.</td></tr>}
            {meetings.map(m => {
              const ex = m.document_extras || {};
              return (
                <tr key={m.id} onClick={() => setDetail(m)} className="cursor-pointer hover:bg-gray-50/60">
                  <td className="px-4 py-2.5 font-medium text-[#0f1f45]">{fmtDate(m.meeting_date)}</td>
                  <td className="px-4 py-2.5 text-gray-500">{ex.sub_type || (m.meeting_type === 'PARTNERS_CIRCULATION' ? 'Meeting by Circulation' : "Partners' Meeting")}</td>
                  <td className="px-4 py-2.5 text-gray-500">{m.decision_mode || '—'}</td>
                  <td className="px-4 py-2.5 text-gray-500">{(m.agenda || []).length} item(s) · {(m.decisions || []).length} decision(s)</td>
                  <td className="px-4 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600">{m.status || 'Draft'}</span>
                  </td>
                  <td className="px-4 py-2.5 text-right" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setEditing(m)} className="text-[11px] font-semibold text-blue-600 mr-3">Edit</button>
                    <button onClick={() => remove(m)} className="text-[11px] font-semibold text-red-500">Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY = {
  sub_type: "Partners' Meeting",
  meeting_date: '', meeting_time: '', venue: '', notice_date: '',
  chairperson: '', decision_mode: DECISION_MODES[0], status: 'Draft',
  attendees: [], agenda: [], decisions: [], minutes: '',
};

function MeetingForm({ company, partners, meeting, onCancel, onSaved }) {
  const [form, setForm] = useState(() => meeting ? {
    sub_type: (meeting.document_extras || {}).sub_type || (meeting.meeting_type === 'PARTNERS_CIRCULATION' ? 'Meeting by Circulation' : "Partners' Meeting"),
    meeting_date: meeting.meeting_date ? String(meeting.meeting_date).slice(0, 10) : '',
    meeting_time: meeting.meeting_time || '',
    venue: meeting.venue || '',
    notice_date: meeting.notice_date ? String(meeting.notice_date).slice(0, 10) : '',
    chairperson: meeting.chairperson || '',
    decision_mode: meeting.decision_mode || DECISION_MODES[0],
    status: meeting.status || 'Draft',
    attendees: Array.isArray(meeting.attendees) ? meeting.attendees : [],
    agenda: Array.isArray(meeting.agenda) ? meeting.agenda : [],
    decisions: Array.isArray(meeting.decisions) ? meeting.decisions : [],
    minutes: meeting.minutes || '',
  } : { ...EMPTY });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleAttendee = (name) => set('attendees',
    form.attendees.includes(name) ? form.attendees.filter(a => a !== name) : [...form.attendees, name]);

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        company_id: company.id,
        meeting_type: storeType(form.sub_type),
        meeting_date: form.meeting_date || null,
        meeting_time: form.meeting_time || null,
        venue: form.venue || null,
        notice_date: form.notice_date || null,
        chairperson: form.chairperson || null,
        decision_mode: form.decision_mode || null,
        status: form.status || 'Draft',
        attendees: form.attendees,
        agenda: form.agenda,
        decisions: form.decisions,
        minutes: form.minutes || null,
        document_extras: { sub_type: form.sub_type },
      };
      const url = meeting ? `${BASE}/api/mca/meetings/${meeting.id}` : `${BASE}/api/mca/meetings`;
      const res = await fetch(url, { method: meeting ? 'PUT' : 'POST', headers: jsonHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { alert(data.message || 'Could not save'); return; }
      onSaved(data);
    } catch (e) { alert('Server error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-[#0f1f45]">{meeting ? 'Edit' : 'Record'} Partners' Meeting</p>
        <button onClick={onCancel} className="text-xs text-gray-400">Cancel</button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Field label="Type">
          <select value={form.sub_type} onChange={e => set('sub_type', e.target.value)} className="in">
            {SUB_TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
        </Field>
        <Field label="Meeting date"><input type="date" value={form.meeting_date} onChange={e => set('meeting_date', e.target.value)} className="in" /></Field>
        <Field label="Time"><input type="time" value={form.meeting_time} onChange={e => set('meeting_time', e.target.value)} className="in" /></Field>
        <Field label="Notice issued on"><input type="date" value={form.notice_date} onChange={e => set('notice_date', e.target.value)} className="in" /></Field>
        <Field label="Venue / mode"><input value={form.venue} onChange={e => set('venue', e.target.value)} placeholder="Registered office / video" className="in" /></Field>
        <Field label="Chairperson">
          <select value={form.chairperson} onChange={e => set('chairperson', e.target.value)} className="in">
            <option value="">—</option>
            {partners.map(p => <option key={p.id} value={p.director_name}>{p.director_name}</option>)}
          </select>
        </Field>
        <Field label="Decision mode">
          <select value={form.decision_mode} onChange={e => set('decision_mode', e.target.value)} className="in">
            {DECISION_MODES.map(m => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Status">
          <select value={form.status} onChange={e => set('status', e.target.value)} className="in">
            {STATUSES.map(s => <option key={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">Partners present</p>
        <div className="flex flex-wrap gap-2">
          {partners.length === 0 && <span className="text-xs text-gray-400">No partners on record for this LLP.</span>}
          {partners.map(p => (
            <button key={p.id} onClick={() => toggleAttendee(p.director_name)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${form.attendees.includes(p.director_name) ? 'bg-[#0f1f45] text-white border-[#0f1f45]' : 'bg-white text-gray-500 border-gray-200'}`}>
              {p.director_name}{p.is_designated_partner ? ' · DP' : ''}
            </button>
          ))}
        </div>
      </div>

      <ListEditor label="Agenda items" items={form.agenda} onChange={v => set('agenda', v)} placeholder="e.g. Admission of Mr. X as a partner" />
      <ListEditor label="Decisions / resolutions passed" items={form.decisions} onChange={v => set('decisions', v)} placeholder="e.g. RESOLVED that the contribution of the LLP be increased to ₹…" />

      <Field label="Minutes (free text)">
        <textarea value={form.minutes} onChange={e => set('minutes', e.target.value)} rows={4} className="in" placeholder="Narrative minutes, if maintained separately." />
      </Field>

      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onCancel} className="px-3.5 py-2 rounded-lg text-xs font-semibold text-gray-500 border border-gray-200">Cancel</button>
        <button onClick={save} disabled={saving} className="px-3.5 py-2 rounded-lg text-xs font-semibold text-white bg-[#0f1f45] disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
      </div>

      <style>{`.in{width:100%;padding:7px 9px;border:1px solid #e2e8f0;border-radius:8px;font-size:13px}`}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[11px] font-semibold text-gray-500 uppercase">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function ListEditor({ label, items, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  return (
    <div className="mt-4">
      <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">{label}</p>
      <div className="space-y-1.5">
        {(items || []).map((it, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-xs text-gray-300 w-5 text-right">{i + 1}.</span>
            <input value={it} onChange={e => onChange(items.map((x, xi) => xi === i ? e.target.value : x))}
              className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs" />
            <button onClick={() => onChange(items.filter((_, xi) => xi !== i))} className="text-red-400 text-xs">✕</button>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <input value={draft} onChange={e => setDraft(e.target.value)} placeholder={placeholder}
          onKeyDown={e => { if (e.key === 'Enter' && draft.trim()) { onChange([...(items || []), draft.trim()]); setDraft(''); } }}
          className="flex-1 px-2.5 py-1.5 border border-gray-200 rounded-lg text-xs" />
        <button onClick={() => { if (draft.trim()) { onChange([...(items || []), draft.trim()]); setDraft(''); } }}
          className="px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#0f1f45] border border-gray-200">Add</button>
      </div>
    </div>
  );
}

function MeetingDetail({ meeting, company, partners, onBack, onEdit }) {
  const ex = meeting.document_extras || {};
  const subType = ex.sub_type || (meeting.meeting_type === 'PARTNERS_CIRCULATION' ? 'Meeting by Circulation' : "Partners' Meeting");
  const dpNames = partners.filter(p => p.is_designated_partner).map(p => p.director_name);

  const printDoc = (kind) => {
    const rows = kind === 'notice' ? (meeting.agenda || []) : (meeting.decisions || []);
    const heading = kind === 'notice' ? 'NOTICE OF MEETING OF PARTNERS' : 'MINUTES OF MEETING OF PARTNERS';
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${heading}</title>
      <style>body{font-family:'Plus Jakarta Sans',sans-serif;font-size:12pt;margin:40px;color:#000}
      h1{font-size:13.5pt;text-align:center;text-transform:uppercase}
      .c{text-align:center}.b{font-weight:bold}ol{margin-top:6px}li{margin-bottom:6px;text-align:justify}
      .sig{margin-top:60px}</style></head><body>
      <h1>${company.company_name}</h1>
      <p class="c">${company.cin_llpin || ''}<br/>${company.registered_office || ''}</p>
      <h1>${heading}</h1>
      <p>${kind === 'notice'
        ? `Notice is hereby given that a ${subType} of the partners of ${company.company_name} will be held on ${fmtDate(meeting.meeting_date)}${meeting.meeting_time ? ` at ${meeting.meeting_time}` : ''}${meeting.venue ? ` at ${meeting.venue}` : ''} to transact the following business:`
        : `Minutes of the ${subType} of the partners of ${company.company_name} held on ${fmtDate(meeting.meeting_date)}${meeting.meeting_time ? ` at ${meeting.meeting_time}` : ''}${meeting.venue ? ` at ${meeting.venue}` : ''}.`}</p>
      ${kind === 'minutes' ? `<p><span class="b">Partners present:</span> ${(meeting.attendees || []).join(', ') || '—'}<br/>
        <span class="b">Chairperson:</span> ${meeting.chairperson || '—'}<br/>
        <span class="b">Decision mode:</span> ${meeting.decision_mode || '—'}</p>` : ''}
      <ol>${rows.map(r => `<li>${String(r).replace(/</g, '&lt;')}</li>`).join('') || '<li>—</li>'}</ol>
      ${kind === 'minutes' && meeting.minutes ? `<p>${String(meeting.minutes).replace(/</g, '&lt;').replace(/\n/g, '<br/>')}</p>` : ''}
      <p class="b">By order of the partners</p>
      <div class="sig">_____________________________<br/>${meeting.chairperson || (dpNames[0] || 'Designated Partner')}<br/>Designated Partner</div>
      </body></html>`;
    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5">
      <div className="flex items-center justify-between mb-4">
        <button onClick={onBack} className="text-xs text-gray-400">← Back</button>
        <div className="flex gap-2">
          <button onClick={() => printDoc('notice')} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#0f1f45] border border-gray-200">Notice</button>
          <button onClick={() => printDoc('minutes')} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-[#0f1f45] border border-gray-200">Minutes</button>
          <button onClick={onEdit} className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-[#0f1f45]">Edit</button>
        </div>
      </div>

      <p className="text-base font-bold text-[#0f1f45]">{subType} · {fmtDate(meeting.meeting_date)}</p>
      <p className="text-xs text-gray-400 mb-4">
        {meeting.venue || '—'}{meeting.meeting_time ? ` · ${meeting.meeting_time}` : ''} · Decision mode: {meeting.decision_mode || '—'} · Status: {meeting.status || 'Draft'}
      </p>

      <div className="grid grid-cols-2 gap-5">
        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">Agenda</p>
          <ol className="list-decimal ml-5 text-sm text-gray-700 space-y-1">
            {(meeting.agenda || []).map((a, i) => <li key={i}>{a}</li>)}
            {(meeting.agenda || []).length === 0 && <p className="text-xs text-gray-400 -ml-5">No agenda recorded.</p>}
          </ol>
        </div>
        <div>
          <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">Decisions</p>
          <ol className="list-decimal ml-5 text-sm text-gray-700 space-y-1">
            {(meeting.decisions || []).map((a, i) => <li key={i}>{a}</li>)}
            {(meeting.decisions || []).length === 0 && <p className="text-xs text-gray-400 -ml-5">No decisions recorded.</p>}
          </ol>
        </div>
      </div>

      <div className="mt-4">
        <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">Partners present</p>
        <p className="text-sm text-gray-700">{(meeting.attendees || []).join(', ') || '—'}</p>
      </div>

      {meeting.minutes && (
        <div className="mt-4">
          <p className="text-[11px] font-semibold text-gray-500 uppercase mb-1.5">Minutes</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{meeting.minutes}</p>
        </div>
      )}
    </div>
  );
}
